import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, FileText, AlertCircle, Loader, LogOut, Menu, ChevronLeft, CheckSquare, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function DocumentViewer() {
  const { encryptedId, docId } = useParams();
  const navigate = useNavigate();
  const [showConfidentialityModal, setShowConfidentialityModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [document, setDocument] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [logging, setLogging] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [hasInteractedWithModal, setHasInteractedWithModal] = useState(false);
  const [isModalMinimized, setIsModalMinimized] = useState(false);

  const iframeRef = useRef(null);

  // Fetch employee data
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/employee/${encryptedId}`);
        if (!response.ok) throw new Error('Failed to fetch employee');
        
        const data = await response.json();
        if (data.employee) {
          setEmployee(data.employee);
          sessionStorage.setItem('employeeId', data.employee.display_emp_id);
          sessionStorage.setItem('encryptedId', encryptedId);

          const adminIds = ['0000', '0010', '12345']; // Example admin IDs
          const admin = adminIds.includes(Number(data.employee.display_emp_id));
          setIsAdmin(admin);

          // if (!admin) {
            setShowConfidentialityModal(true);
          // }
        }
      } catch (err) {
        console.error('Error fetching employee:', err);
        setError('Failed to load employee data');
      }
    };
    fetchEmployeeData();
  }, [encryptedId]);

  // Fetch document data
  const fetchDocument = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/policy/${docId}/viewer`);
      if (!res.ok) throw new Error('Failed to fetch document');
      
      const data = await res.json();
      setDocument(data.document);
      setLoading(false);
    } catch (err) {
      console.error('Error loading document:', err);
      setError('Failed to load document');
      setLoading(false);
    }
  }, [docId]);

  useEffect(() => {
    fetchDocument();
  }, [fetchDocument]);

// Track PDF viewing: Show modal when user scrolls to bottom of full page
useEffect(() => {
  // if (!isAdmin && document && !showConfidentialityModal && !hasInteractedWithModal) {
  if (!showConfidentialityModal && !hasInteractedWithModal) {
    const iframe = iframeRef.current;

    // Detect when iframe is fully scrolled into view (bottom reached)
    const handlePageScroll = () => {
      if (!iframe) return;

      const rect = iframe.getBoundingClientRect();

      // If iframe bottom is inside the viewport → user reached the end
      if (rect.bottom <= window.innerHeight + 5) {
        if (!hasScrolledToBottom) {
          console.log("Reached bottom of PDF → Showing modal");
          setHasScrolledToBottom(true);
          setShowTermsModal(true);
          setIsModalMinimized(false);
        }
      }
    };

    window.addEventListener("scroll", handlePageScroll);

    // On iframe load log only (PDF scroll cannot be tracked)
    const onIframeLoad = () => {
      console.log("PDF loaded – cannot track scroll inside PDF (CORS)");
    };
    iframe?.addEventListener("load", onIframeLoad);

    return () => {
      window.removeEventListener("scroll", handlePageScroll);
      iframe?.removeEventListener("load", onIframeLoad);
    };
  }
}, [document, isAdmin, showConfidentialityModal, hasScrolledToBottom, hasInteractedWithModal]);


  const handleConfidentialityAccept = async () => {
    setLogging(true);
    try {
      let ipAddress = 'unknown';
      try {
        const ipResponse = await fetch('https://api.ipify.org?format=json');
        const ipData = await ipResponse.json();
        ipAddress = ipData.ip;
      } catch (err) {
        console.warn('Could not get IP:', err);
      }

      const deviceInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenResolution: `${window.innerWidth}x${window.innerHeight}`,
        timestamp: new Date().toISOString()
      };

      const employeeId = sessionStorage.getItem('employeeId');
      
      if (!employeeId) {
        throw new Error('Employee ID not found in session');
      }

      const response = await fetch(`${API_URL}/api/policy/view`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          employee_id: employeeId,
          document_id: parseInt(docId),
          ip_address: ipAddress,
          device_info: deviceInfo,
          // is_admin: false,
          user_read_accept: 'no' // Initial value is 'no'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to log view');
      }

      console.log('✓ Document view logged successfully');
      setShowConfidentialityModal(false);
    } catch (error) {
      console.error('Error logging view:', error);
      setShowConfidentialityModal(false);
    } finally {
      setLogging(false);
    }
  };

  // Update handleTermsAccept function
const handleTermsAccept = async () => {
  setLogging(true);
  try {
    const employeeId = sessionStorage.getItem('employeeId');
    
    if (!employeeId) {
      throw new Error('Employee ID not found in session');
    }

    const response = await fetch(`${API_URL}/api/policy/view/accept`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        employee_id: employeeId,
        document_id: parseInt(docId)
      })
    });

    if (!response.ok) {
      throw new Error('Failed to record acceptance');
    }

    console.log('✓ Terms acceptance recorded successfully');
    setShowTermsModal(false);
    setHasInteractedWithModal(true); // Mark as interacted
    setIsModalMinimized(false);
  } catch (error) {
    console.error('Error recording acceptance:', error);
    alert('Failed to record your acceptance. Please try again.');
  } finally {
    setLogging(false);
  }
};

// Add new function for decline
const handleTermsDecline = () => {
  setShowTermsModal(false);
  setIsModalMinimized(false);
  // Don't set hasInteractedWithModal to true, so modal can appear again
};

// Add new function for minimize
const handleMinimize = () => {
  setIsModalMinimized(true);
  setShowTermsModal(false);
};

// Add new function for maximize
const handleMaximize = () => {
  setIsModalMinimized(false);
  setShowTermsModal(true);
};

  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-12 h-12 border-4 border-indigo-300 border-t-indigo-600 rounded-full"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white rounded-lg shadow-xl p-8 max-w-md text-center"
        >
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Error</h1>
          <p className="text-gray-600">{error}</p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(-1)}
            className="mt-4 px-6 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700"
          >
            Go Back
          </motion.button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="bg-white shadow-md py-4 px-6 sticky top-0 z-40"
      >
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4 flex-1">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg"
            >
              <ChevronLeft className="w-6 h-6 text-gray-700" />
            </motion.button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">{document?.title}</h1>
              {employee && (
                <p className="text-sm text-gray-600 mt-1">
                  Accessed by: {employee.name} {isAdmin && '(Admin)'}
                </p>
              )}
            </div>
          </div>
          
          <div className="relative">
            <motion.button
              whileHover={{ scale: 1.05 }}
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold hover:bg-indigo-700"
            >
              {employee?.name?.charAt(0) || 'U'}
            </motion.button>
            <AnimatePresence>
              {userMenuOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50"
                >
                  <div className="p-4 border-b border-gray-200">
                    <p className="font-semibold text-gray-900">{employee?.name}</p>
                    <p className="text-sm text-gray-500">{employee?.email}</p>
                    <p className="text-xs text-gray-400 mt-1">ID: {employee?.display_emp_id}</p>
                    {isAdmin && <p className="text-xs text-red-600 mt-1 font-semibold">👤 ADMIN</p>}
                  </div>
                  <motion.button
                    whileHover={{ bg: '#f3f4f6' }}
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-red-600 hover:bg-gray-100 flex items-center gap-2 font-medium"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </motion.button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      {/* Confidentiality Modal */}
      <AnimatePresence>
        {/* {showConfidentialityModal && !isAdmin && ( */}
        {showConfidentialityModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg shadow-2xl max-w-md p-8"
            >
              <div className="text-center mb-6">
                <CheckCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-800 mb-2">⚠️ Confidentiality Notice</h2>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6 max-h-48 overflow-y-auto">
                <p className="text-sm text-gray-700 leading-relaxed">
                  This document contains confidential and proprietary information. By accessing this document, you agree to:
                  <br /><br />
                  • Keep the content strictly confidential
                  <br />
                  • Not share with unauthorized personnel
                  <br />
                  • Comply with all company policies
                  <br />
                  • Understand that all access is logged and monitored
                  <br /><br />
                  Violation may result in disciplinary action up to and including termination.
                </p>
              </div>

              <div className="flex gap-4">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate(-1)}
                  className="flex-1 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium rounded-lg transition-colors"
                >
                  Decline
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleConfidentialityAccept}
                  disabled={logging}
                  className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {logging ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'I Accept'
                  )}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Terms & Conditions Acceptance Modal (Bottom Animated Popup) */}
{/* Terms & Conditions Acceptance Modal (Bottom Animated Popup) */}
<AnimatePresence>
  {/* {showTermsModal && !isAdmin && ( */}
  {showTermsModal && (
    <motion.div
      initial={{ y: 100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: 100, opacity: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-indigo-600 to-purple-600 shadow-2xl z-50"
    >
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center flex-shrink-0">
            <CheckSquare className="w-7 h-7 text-indigo-600" />
          </div>
          <div className="flex-1 text-white">
            <h3 className="text-2xl font-bold mb-2">
              📋 Complete Your Policy Review
            </h3>
            <p className="text-indigo-100 mb-3 text-lg">
              You have finished reading this policy document. Please confirm your acceptance of the terms and conditions.
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4 border border-white/20">
              <p className="text-white font-semibold mb-2">By clicking "Accept", you confirm that:</p>
              <ul className="text-indigo-100 space-y-1 ml-4">
                <li>✓ I have read and understood the entire policy document</li>
                <li>✓ I agree to comply with all terms and conditions</li>
                <li>✓ I understand this acceptance is legally binding and recorded</li>
              </ul>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleTermsAccept}
              disabled={logging}
              className="px-8 py-3 bg-white text-indigo-600 font-bold rounded-lg shadow-lg hover:shadow-xl transition disabled:opacity-50 flex items-center justify-center gap-2 min-w-[200px]"
            >
              {logging ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Recording...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Accept
                </>
              )}
            </motion.button>
            <motion.button
              style={{ display: "none" }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleTermsDecline}
              className="px-6 py-2 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-lg transition backdrop-blur-sm"
            >
              Decline
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleMinimize}
              className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white font-medium rounded-lg transition backdrop-blur-sm flex items-center justify-center gap-2"
            >
              <ChevronDown className="w-4 h-4" />
              Minimize
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  )}
</AnimatePresence>

{/* Minimized Modal Button (Bottom Right Corner) */}
<AnimatePresence>
  {/* {isModalMinimized && !isAdmin && hasScrolledToBottom && !hasInteractedWithModal && ( */}
  {isModalMinimized && hasScrolledToBottom && !hasInteractedWithModal && (
    <motion.button
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0, opacity: 0 }}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      onClick={handleMaximize}
      className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-full shadow-2xl flex items-center justify-center z-50 border-4 border-white hover:shadow-indigo-500/50 transition-all"
    >
      <div className="relative">
        <CheckSquare className="w-8 h-8 text-white" />
        <motion.div
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white"
        />
      </div>
    </motion.button>
  )}
</AnimatePresence>

      {(!showConfidentialityModal || isAdmin) && document && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="max-w-7xl mx-auto p-6"
        >
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {document.file_url ? (
              <div
                onContextMenu={(e) => e.preventDefault()}
                onKeyDown={(e) => {
                  if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 's')) {
                    e.preventDefault();
                  }
                }}
                style={{
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  msUserSelect: 'none'
                }}
              >
                <iframe
                  ref={iframeRef}
                  src={`${API_URL}${document.file_url}#toolbar=0`}
                  title={document.title}
                  width="100%"
                  height="800"
                  style={{ 
                    border: 'none',
                    pointerEvents: 'auto'
                  }}
                  className="w-full"
                  allow="fullscreen"
                  referrerPolicy="no-referrer"
                />
              </div>
            ) : (
              <div className="p-8 text-center min-h-96 flex flex-col items-center justify-center">
                <FileText className="w-16 h-16 text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg">Document file not available</p>
                <p className="text-gray-400 text-sm mt-2">{document.file_path}</p>
              </div>
            )}
          </div>
          
          <div className="mt-6 bg-white rounded-lg shadow-md p-4 text-sm text-gray-600">
            <p><strong>Document:</strong> {document.title}</p>
            <p><strong>Type:</strong> {document.file_type}</p>
            <p><strong>Accessed:</strong> {new Date().toLocaleString()}</p>
            {isAdmin && <p className="text-red-600 mt-2"><strong>⚠️ Admin View - Not Logged</strong></p>}
            {!isAdmin && <p className="text-green-600 mt-2"><strong>✅ User View - Logged</strong></p>}
            {!isAdmin && hasScrolledToBottom && !showTermsModal && <p className="text-blue-600 mt-1"><strong>📋 Terms Accepted ✓</strong></p>}
          </div>
        </motion.div>
      )}
    </div>
  );
}